import React from 'react';
import DisclaimerText from './DisclaimerText';

const SwingHelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-amber-600 px-6 py-4 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-white">📖 How to Use: Swing Trading Model</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-6">
            {/* Introduction */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">🎯 Overview</h3>
              <p className="text-gray-700">
                The Swing Trading Model identifies short-to-medium term trading opportunities (1-3 weeks) using 
                Bollinger Bands and multi-timeframe RSI confirmation. It categorizes stocks into four types: 
                <strong> CALL Setups</strong> (bullish entries), <strong>PUT Setups</strong> (bearish entries), 
                <strong>Near CALL</strong> (potential bullish), and <strong>Near PUT</strong> (potential bearish).
              </p>
            </section>

            {/* Strategy Explanation */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">📊 Trading Strategy</h3>
              
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border-l-4 border-green-500">
                  <h4 className="font-semibold text-green-900 mb-2">🟢 CALL Setup (Bullish Entry)</h4>
                  <p className="text-green-800 text-sm mb-2">Strong buy signal - all conditions met:</p>
                  <ul className="list-disc list-inside text-green-800 text-sm ml-2 space-y-1">
                    <li><strong>Bollinger Breakout:</strong> Price crosses above upper band</li>
                    <li><strong>Hourly RSI &gt; 60:</strong> Strong short-term momentum</li>
                    <li><strong>Daily RSI &gt; 60:</strong> Medium-term confirmation</li>
                    <li><strong>Weekly RSI &gt; 60:</strong> Long-term trend support</li>
                  </ul>
                </div>

                <div className="p-4 bg-red-50 border-l-4 border-red-500">
                  <h4 className="font-semibold text-red-900 mb-2">🔴 PUT Setup (Bearish Entry)</h4>
                  <p className="text-red-800 text-sm mb-2">Strong sell signal - all conditions met:</p>
                  <ul className="list-disc list-inside text-red-800 text-sm ml-2 space-y-1">
                    <li><strong>Bollinger Breakdown:</strong> Price crosses below lower band</li>
                    <li><strong>Hourly RSI &lt; 40:</strong> Weak short-term momentum</li>
                    <li><strong>Daily RSI &lt; 40:</strong> Medium-term weakness</li>
                    <li><strong>Weekly RSI &lt; 40:</strong> Long-term downtrend</li>
                  </ul>
                </div>

                <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500">
                  <h4 className="font-semibold text-yellow-900 mb-2">🟡 Near CALL (Potential Bullish)</h4>
                  <p className="text-yellow-800 text-sm">
                    RSI conditions met but no Bollinger breakout yet. These stocks are "ready" and waiting for 
                    the trigger. Watch closely for breakout to enter.
                  </p>
                </div>

                <div className="p-4 bg-orange-50 border-l-4 border-orange-500">
                  <h4 className="font-semibold text-orange-900 mb-2">🟠 Near PUT (Potential Bearish)</h4>
                  <p className="text-orange-800 text-sm">
                    Weak RSI across timeframes but no Bollinger breakdown yet. These stocks are vulnerable. 
                    Watch for breakdown to enter short position.
                  </p>
                </div>
              </div>
            </section>

            {/* Best Breakout */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">⭐ Best Breakout Stock</h3>
              <p className="text-gray-700 mb-2">
                The model automatically identifies the single best CALL setup using a scoring algorithm:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                <li><strong>Base Score:</strong> 100 points</li>
                <li><strong>RSI Strength:</strong> +20 points for RSI above 60</li>
                <li><strong>Price Momentum:</strong> +20 points for strong upward movement</li>
                <li><strong>Price Range:</strong> +10 points if price between ₹100-₹2000</li>
              </ul>
              <p className="text-gray-700 mt-2">
                This is the <strong>highest conviction trade</strong> based on technical analysis.
              </p>
            </section>

            {/* Configure Parameters */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">⚙️ Configure Analysis Parameters</h3>
              
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">📊 Stock Selection</h4>
                <p className="text-gray-600 text-sm mb-3">
                  Choose your stock universe:
                </p>
                <ul className="list-disc list-inside text-gray-600 text-sm ml-4 space-y-1">
                  <li><strong>Predefined Indices:</strong> NIFTY 50, NIFTY 200, NIFTY MIDCAP 50, etc.</li>
                  <li><strong>Upload CSV:</strong> Analyze your own custom watchlist</li>
                </ul>
              </div>
            </section>

            {/* CSV Upload */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">📤 Upload Custom Stock List (CSV)</h3>
              <p className="text-gray-700 mb-2">
                To analyze your own stocks:
              </p>
              <ol className="list-decimal list-inside text-gray-600 space-y-1 ml-4">
                <li>Create a CSV file with a <strong>"Symbol"</strong> column</li>
                <li>Add symbols: <code className="bg-gray-100 px-2 py-1 rounded">RELIANCE.NSE</code>, <code className="bg-gray-100 px-2 py-1 rounded">TCS.NSE</code></li>
                <li>Click "Upload CSV File" and select your file</li>
                <li>Run the analysis</li>
              </ol>
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  <strong>Example CSV format:</strong>
                  <br/>
                  <code className="text-xs bg-white px-2 py-1 rounded mt-1 inline-block">
                    Symbol<br/>
                    RELIANCE.NSE<br/>
                    TCS.NSE<br/>
                    INFY.NSE
                  </code>
                </p>
              </div>
            </section>

            {/* Understanding Results */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">📊 Understanding the Results</h3>
              <p className="text-gray-700 mb-2">
                Results are organized into clear categories:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                <li><strong>Best Breakout:</strong> Top recommendation with score and metrics</li>
                <li><strong>CALL Setups:</strong> Stocks ready for bullish entries (green section)</li>
                <li><strong>PUT Setups:</strong> Stocks ready for bearish entries (red section)</li>
                <li><strong>Near CALL:</strong> Stocks approaching bullish setup (yellow section)</li>
                <li><strong>Near PUT:</strong> Stocks approaching bearish setup (orange section)</li>
              </ul>
            </section>

            {/* Trading Action */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">🎯 Taking Action</h3>
              <p className="text-gray-700 mb-2">
                For each stock in the results:
              </p>
              <ol className="list-decimal list-inside text-gray-600 space-y-1 ml-4">
                <li>Click the <strong>"Trade"</strong> button to open the Order Ticket</li>
                <li>Configure your trade parameters</li>
                <li>Place orders through your connected broker</li>
                <li>Download results as Excel for tracking</li>
              </ol>
            </section>

            {/* Pro Tips */}
            <section className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">💡 Pro Tips</h3>
              <ul className="list-disc list-inside text-blue-800 space-y-1 text-sm ml-4">
                <li><strong>Best Breakout:</strong> Highest probability - consider this first</li>
                <li><strong>CALL Setups:</strong> Enter immediately or on minor pullback</li>
                <li><strong>PUT Setups:</strong> Use for hedging or short positions</li>
                <li><strong>Near Setups:</strong> Set price alerts for breakout/breakdown triggers</li>
                <li><strong>Time Horizon:</strong> Hold swing trades for 1-3 weeks typically</li>
                <li><strong>Stop Loss:</strong> Place stops below/above recent support/resistance</li>
              </ul>
            </section>

            {/* Risk Warning */}
            <section className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-red-900 mb-2">⚠️ Risk Disclaimer</h3>
              <p className="text-red-800 text-sm">
                Swing trading involves significant risk. Technical indicators can generate false signals. 
                Always use proper risk management (stop losses, position sizing). Past performance does not 
                guarantee future results. Never risk more than you can afford to lose.
              </p>
            </section>

            {/* Platform Disclaimer */}
            <DisclaimerText />
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t">
            <button
              onClick={onClose}
              className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
            >
              Got it! Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SwingHelpModal;

